import { useState, FC } from 'react';
import { ChevronDown, ChevronRight, Copy, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { TableCell, TableRow } from '@features/ui';
import { TCouponCode } from '@src/types';

import { TransactionPanel } from './TransactionPanel';

const COL_SPAN = 5;

interface CodeRowProps {
  code: TCouponCode;
  onEdit: (code: TCouponCode) => void;
  onDelete: (code: TCouponCode) => void;
}

export const CodeRow: FC<CodeRowProps> = ({ code, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code.code).then(() => {
      toast.success('Code copied!');
    });
  };

  return (
    <>
      <TableRow className="border-b border-gray-100 transition-colors">
        {/* Code */}
        <TableCell className="py-2.5 px-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-medium">{code.code}</span>
            <button
              className="text-gray-400 hover:cursor-pointer transition-colors"
              title="Copy code"
              type="button"
              onClick={handleCopy}
            >
              <Copy size={13} />
            </button>
          </div>
        </TableCell>

        {/* Used for */}
        <TableCell className="py-2.5 px-3">
          <button
            className="flex items-center gap-1 text-sm hover:cursor-pointer transition-colors"
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <span>
              Coupon used for{' '}
              <span className="font-medium">{code.used_count}</span>{' '}
              {code.used_count === 1 ? 'transaction' : 'transactions'}
            </span>
          </button>
        </TableCell>

        {/* User */}
        <TableCell className="py-2.5 px-3 text-sm">
          {code.assigned_user_id != null ? (
            <button
              className="flex items-center gap-1 text-blue hover:cursor-pointer transition-colors"
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? (
                <ChevronDown size={13} />
              ) : (
                <ChevronRight size={13} />
              )}
              <span>User #{code.assigned_user_id}</span>
            </button>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </TableCell>

        {/* Affiliate */}
        <TableCell className="py-2.5 px-3 text-sm">
          {code.aff_id != null ? (
            <span>{code.aff_id}</span>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </TableCell>

        {/* Actions */}
        <TableCell className="py-2.5 px-3">
          <div className="flex items-center gap-2">
            <button
              className="text-gray-400 hover:text-blue hover:cursor-pointer transition-colors"
              title="Edit"
              type="button"
              onClick={() => onEdit(code)}
            >
              <Pencil size={14} />
            </button>
            <button
              className="text-gray-400 hover:text-red-500 hover:cursor-pointer transition-colors"
              title="Delete"
              type="button"
              onClick={() => onDelete(code)}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell className="p-0" colSpan={COL_SPAN}>
            <TransactionPanel codeId={code.id} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
